import { useState, useRef, useEffect, useCallback } from 'react';
import * as Tone from 'tone';
import { INSTRUMENT_RANGES } from '../data/theoryConstants';
import { getSynthForInstrument } from '../audio/SynthFactory';
import { INSTRUMENT_LIBRARY, getInstrument } from '../data/InstrumentRegistry';
import { createChannelStrip, createMasteringChain } from '../audio/EffectChain';
import { applyAutoMix } from '../audio/MixerStrategies';
import { MixAgent, MixRoles } from '../audio/MixAgent';

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

const MAX_FILTER_FREQ = 20000;
const MIN_FILTER_FREQ = 500;
const MAX_EVENTS = 60000;
const KEYSWITCH_MAX_NOTE = 24; // MIDI 0-23 are considered Keyswitches

/**
 * useAudioEngine Hook
 * Manages Tone.js lifecycle, synth creation, and playback scheduling.
 */
export const useAudioEngine = () => {
    const [isReady, setIsReady] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    // Refs
    const synths = useRef({});
    const parts = useRef([]);
    const masterVol = useRef(null);
    const masteringChain = useRef(null); // Replaces limiter
    const globalReverb = useRef(null);
    const globalDelay = useRef(null);
    const globalDistortion = useRef(null);
    const globalFilter = useRef(null);
    const currentEnsembleRef = useRef('orchestra');
    const lastNoteEventTime = useRef(0);

    // Operation "Auto-Engineer" State
    const mixAgent = useRef(new MixAgent());
    const sidechainNodes = useRef([]); // Store followers/signals for disposal
    const compositionRef = useRef(null); // Store current composition for context awareness

    // Performance Watchdog State
    const performanceStats = useRef({
        lastCheck: 0,
        lagHistory: [],
        warningCount: 0
    });
    const watchdogId = useRef(null);

    // Detect Mobile
    const isMobile = useRef(false);
    useEffect(() => {
        isMobile.current = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
    }, []);

    // Initialize Audio
    // Refactored to use EffectChain factories
    const startAudio = async () => {
        try {
            await Tone.start();

            // Start Performance Watchdog
            startWatchdog();
            if (Tone.context.state !== 'running') await Tone.context.resume();

            console.log('Audio Context Started');

            if (!masterVol.current) {
                // Initialize at -100 (muted) to prevent startup pop, then ramp up
                masterVol.current = new Tone.Volume(-100).toDestination();

                // Initialize Mastering Chain
                masteringChain.current = createMasteringChain();
                masteringChain.current.connect(masterVol.current);

                // Ramp to default volume quickly after init
                // Refactor: Boosted from -10 to -2 to satisfy "Loudness War" requirement (approx -1 dBTP target)
                masterVol.current.volume.rampTo(-2, 0.5);

                // Global Effect Busses (Parallel Architecture)

                // 1. Dry Bus: Filter -> MasteringChain (Clean Signal Hygiene)
                // Removed Global Distortion to preserve transients and reduce mud.
                // Grit should be applied per-instrument if needed.
                globalFilter.current = new Tone.Filter(MAX_FILTER_FREQ, "lowpass");
                // Connect Filter to Mastering Chain Input
                globalFilter.current.connect(masteringChain.current.input);

                // 2. Wet/Ambience Bus: Delay -> Reverb -> MasteringChain (Parallel to Dry)
                if (isMobile.current) {
                    // Mobile Optimization: Use Compressor as "Fake Reverb"
                    globalReverb.current = new Tone.Compressor({
                        threshold: -30,
                        ratio: 3,
                        attack: 0.1,
                        release: 0.8
                    });
                } else {
                    globalReverb.current = new Tone.Reverb({ decay: 2, wet: 0.2 });
                    await globalReverb.current.generate();
                }
                // Connect Reverb to Mastering Chain Input
                globalReverb.current.connect(masteringChain.current.input);

                globalDelay.current = new Tone.FeedbackDelay("8n.", 0.2).connect(globalReverb.current);
            }

            setIsReady(true);
            return true;
        } catch (e) {
            console.error('Failed to start audio:', e);
            return false;
        }
    };

    // Helper: Update Synths based on active instruments state (Mute/Solo/Volume/Delay)
    const setEnsemble = (activeInstruments) => {
        if (!Array.isArray(activeInstruments)) return;

        const activeKeys = new Set();
        const hasSolo = activeInstruments.some(i => i.soloed);

        // 1. Update Existing / Create New
        activeInstruments.forEach(inst => {
            if (!inst || typeof inst !== 'object') return;

            // Find synth by unique ID (Instance ID > ID > Role fallback)
            const uniqueKey = inst.instanceId || inst.id || (inst.roles && inst.roles[0]);
            activeKeys.add(uniqueKey);

            const synth = synths.current[uniqueKey];

            if (synth && !synth.disposed) {
                // Update Volume / Mute / Solo Logic
                let isMuted = inst.muted;
                if (hasSolo && !inst.soloed) isMuted = true;

                // MixAgent handles volume targets, but we respect Mute/Solo override
                if (isMuted) {
                    if (synth.volume && typeof synth.volume.rampTo === 'function') {
                        synth.volume.rampTo(-100, 0.1);
                    }
                }
                // Note: If not muted, MixAgent.updateMix loop will restore levels.
            }
        });

        // 2. Garbage Collect Stale Synths
        Object.keys(synths.current).forEach(key => {
            if (!activeKeys.has(key)) {
                const synth = synths.current[key];
                if (synth) {
                    console.log(`[AudioEngine] disposing stale synth: ${key}`);
                    try {
                        synth.disconnect(); // Disconnect strip output
                        synth.dispose();    // Dispose strip + inner synth
                    } catch (e) {
                        console.warn(`Error disposing synth ${key}`, e);
                    }
                    delete synths.current[key];
                }
            }
        });

        // 3. "Clarity Protocol" (Auto-Sidechaining)
        setupSidechain();
    };

    // Task 3: The "Clarity" Protocol
    const setupSidechain = () => {
        // Cleanup old nodes
        sidechainNodes.current.forEach(n => n.dispose());
        sidechainNodes.current = [];

        // Identify Anchor (Kick)
        // We look for a registered Anchor in MixAgent or scan synths
        const registry = mixAgent.current.registry;
        let anchorStrip = null;

        // Find the "Best" Anchor (Prefer Kick over Sub)
        const anchorId = Object.keys(registry).find(key => {
            const entry = registry[key];
            // Check if it's a Kick Drum (Role Anchor + ID check)
            return entry.role === MixRoles.ANCHOR && (key.includes('drum') || key.includes('kick'));
        }) || Object.keys(registry).find(key => registry[key].role === MixRoles.ANCHOR);

        if (anchorId) {
            anchorStrip = registry[anchorId].strip;
        }

        if (!anchorStrip) return;

        console.log(`[AudioEngine] Configuring Sidechain from Anchor: ${anchorId}`);

        // Create Follower on Anchor Input (Pre-Fader / Pre-EQ is best, but Strip Input is easiest)
        const follower = new Tone.Follower(0.05); // Fast tracking
        anchorStrip.input.connect(follower);
        sidechainNodes.current.push(follower);

        // Invert Signal for Ducking (0 to 1 -> 0 to -X dB?)
        // Wait, Gain.gain is linear (1.0). We want to reduce it.
        // If Follower gives 0..1 (Amplitude).
        // We want Gain = 1 - (Envelope * Depth).
        // Tone.Subtract(1) -> ...
        // Easier: Use Tone.Scale to map Envelope (0-1) to Gain Reduction (1.0 to 0.4)
        // Duck by ~8dB -> 0.4 linear.

        const scaler = new Tone.Scale(1.0, 0.4); // Max volume 1.0, Min volume 0.4 (Ducked)
        const signal = new Tone.Signal(0); // Dummy signal?
        // Logic: Tone.Scale maps input min-max to output min-max.
        // Input envelope: 0 (silent) -> 1 (loud).
        // Output gain: 1.0 (silent) -> 0.4 (loud).

        follower.connect(scaler);
        sidechainNodes.current.push(scaler);

        // Connect to Targets (Atmosphere & Support)
        Object.keys(registry).forEach(key => {
            if (key === anchorId) return;
            const { role, strip } = registry[key];

            if ((role === MixRoles.ATMOSPHERE || role === MixRoles.SUPPORT) && strip.sidechainGain) {
                // Connect Control Signal to Gain.gain
                // Gain.gain default is 1. We replace it or multiply?
                // Connecting a signal to AudioParam automates it.
                // Tone.Scale output is AudioRange.
                scaler.connect(strip.sidechainGain.gain);
            }
        });
    };

    // Performance Watchdog & Auto-Engineer Loop
    const startWatchdog = () => {
        if (watchdogId.current) cancelAnimationFrame(watchdogId.current);

        const checkPerformance = () => {
            const now = performance.now();

            // --- Auto-Engineer Update (Every ~100ms or every frame? Every frame is fine for ramps) ---
            if (isPlaying) {
                // 1. Calculate Active Density (Ratio of playing synths vs Max capacity)
                const activeSynths = Object.keys(synths.current).length;
                const density = Math.min(1.0, activeSynths / 8); // Assume 8 is "Full"

                // 2. Get Global Tension
                // If composition has curve, map current Transport time to tension
                let tension = 0.5;
                if (compositionRef.current && compositionRef.current.metadata && compositionRef.current.metadata.tensionCurve) {
                    // This assumes we have access to the curve.
                    // If not, fallback to 0.5.
                    // For now, let's use a simulated tension or assume 0.5 if not passed.
                    // Or check compositionRef.current.intensityCurve (added in previous task)
                    // We don't have direct access to exact array index from time easily without calculation.
                    // Fallback: use Density as proxy for Tension if missing.
                    tension = density;
                }

                // 3. Update Agents
                if (mixAgent.current) mixAgent.current.updateMix(tension);
                if (masteringChain.current && masteringChain.current.updateDynamics) {
                    masteringChain.current.updateDynamics(density);
                }
            }

            if (performanceStats.current.lastCheck > 0) {
                const delta = now - performanceStats.current.lastCheck;
                const frameLag = delta - 16.66; // Expected 60fps frame

                if (frameLag > 50) { // Severe lag
                    performanceStats.current.warningCount++;

                    // --- Dynamic Optimization ---
                    // Map Warning Count to Load Metric (0.0 to 1.0+)
                    // 10 warnings = 1.0 (Critical)
                    const loadMetric = performanceStats.current.warningCount / 10;

                    if (mixAgent.current) {
                        mixAgent.current.optimize(loadMetric);
                    }

                    if (performanceStats.current.warningCount > 20) {
                         console.warn("[AudioEngine] High CPU Load detected. Reducing polyphony.");
                         reducePolyphony();
                         performanceStats.current.warningCount = 15; // Reset slightly but keep pressure high
                    }
                } else {
                    // Decay warning count
                    performanceStats.current.warningCount = Math.max(0, performanceStats.current.warningCount - 0.5);

                    // Update optimization state (allows recovery/unmuting)
                    if (mixAgent.current) {
                        const loadMetric = performanceStats.current.warningCount / 10;
                        mixAgent.current.optimize(loadMetric);
                    }
                }
            }

            performanceStats.current.lastCheck = now;
            watchdogId.current = requestAnimationFrame(checkPerformance);
        };

        watchdogId.current = requestAnimationFrame(checkPerformance);
    };

    const reducePolyphony = () => {
        Object.values(synths.current).forEach(strip => {
            // Strip wraps synth in .source
            const synth = strip.source;
            if (synth) {
                // Check if wrapper or direct
                const target = synth.synth || synth; // Handle SynthWrapper
                if (target.maxPolyphony && target.maxPolyphony > 4) {
                     target.maxPolyphony = Math.max(4, Math.floor(target.maxPolyphony * 0.75));
                }
            }
        });
    };

    // Macro Setter
    const setMacro = (name, val) => {
        if (!globalReverb.current) return;

        // 0-1 scale
        if (name === 'atmosphere') {
            if (globalReverb.current.name === 'Reverb' || globalReverb.current.name === 'JCReverb') {
                 globalReverb.current.wet.rampTo(0.1 + (val * 0.5), 0.1);
                 if (globalReverb.current.decay) globalReverb.current.decay = 1 + (val * 4);
            } else if (globalReverb.current.name === 'Compressor') {
                 globalReverb.current.release.rampTo(0.5 + (val * 1.5), 0.1);
            }
            if (globalDelay.current) globalDelay.current.feedback.value = 0.1 + (val * 0.5);
        } else if (name === 'grit') {
            if (globalDistortion.current) globalDistortion.current.distortion = val * 0.8;
        } else if (name === 'warmth') {
            const freq = MAX_FILTER_FREQ - (val * (MAX_FILTER_FREQ - MIN_FILTER_FREQ));
            if (globalFilter.current) globalFilter.current.frequency.rampTo(Math.max(MIN_FILTER_FREQ, freq), 0.1);
        }
    };

    // Get Synth
    const getSynth = (uniqueKey, role, instrumentId) => {
        if (synths.current[uniqueKey]) return synths.current[uniqueKey];

        let strip;
        try {
            let def = null;
            if (instrumentId) {
                // Fix: Use getInstrument to support Hardware & Software libraries
                def = getInstrument(instrumentId);
            }

            const synth = getSynthForInstrument(def, role, isMobile.current);

            // Prepare Mix Traits and Global Sends
            // Inject new Physical Properties (Task 2)
            const mixTraits = def ? { ...def.mixTraits, spectral_floor: def.spectral_floor } : {};
            const globalSends = {
                reverb: globalReverb.current,
                delay: globalDelay.current
            };

            // Wrap in Channel Strip
            strip = createChannelStrip(synth, mixTraits, globalSends);

            // Connect Strip Output to Global Filter Bus (Clean Path)
            // We connect to Filter bus to support the 'Warmth' macro.
            // Global Distortion is removed for signal hygiene.
            if (globalFilter.current) {
                strip.connect(globalFilter.current);
            } else {
                strip.connect(masterVol.current); // Fallback
            }

            synths.current[uniqueKey] = strip;
            return strip;
        } catch (e) {
            console.error(`Synth setup failed for ${uniqueKey}:`, e);
            // Fallback: Create dummy strip or just synth
            const fallbackSynth = new Tone.PolySynth(Tone.Synth).toDestination();
            synths.current[uniqueKey] = fallbackSynth;
            return fallbackSynth;
        }
    };

    // Convert Event Data to Note Name
    const getNoteName = (midiNote) => {
        if (!midiNote) return null;
        const n = Math.round(midiNote);
        const name = NOTE_NAMES[n % 12];
        const oct = Math.floor(n / 12) - 1;
        return name + oct;
    };

    // Schedule Composition
    const scheduleComposition = (composition, onNoteEvent) => {
        // --- SAFETY: CLEANUP ---
        parts.current.forEach(p => {
            if (p) {
                p.stop();
                p.dispose();
            }
        });
        parts.current = [];
        Tone.Transport.cancel();

        if (!composition || !composition.events) return;

        // --- SAFETY: Event Limit Check ---
        // Using strict constant MAX_EVENTS (60k)
        if (composition.events.length > MAX_EVENTS) {
            console.error(`[Playback] Critical Warning: Event count ${composition.events.length} exceeds safety limit (${MAX_EVENTS}). Truncating playback to prevent crash.`);
            composition.events = composition.events.slice(0, 5000);
        }

        // Group events by Instance ID (fallback to ID, then Role)
        const eventsByInst = {};
        composition.events.forEach(ev => {
            const rawKey = ev.instanceId || ev.instrumentId || ev.role || 'unknown';
            const key = String(rawKey); // Force string
            if (!eventsByInst[key]) eventsByInst[key] = [];
            eventsByInst[key].push(ev);
        });

        console.log(`[Playback] Scheduling ${Object.keys(eventsByInst).length} unique tracks.`);

        Object.keys(eventsByInst).forEach(key => {
            const instEvents = eventsByInst[key];
            const role = instEvents[0].role;
            const instrumentId = instEvents[0].instrumentId;

            // Create/Get dedicated synth (ChannelStrip) for this instrument instance
            const currentSynth = getSynth(key, role, instrumentId); // Renamed to currentSynth to match usage below

            // Operation Auto-Engineer: Register with MixAgent
            // Infer instrument definition from ID or pass simple object
            const instDef = getInstrument(instrumentId) || { id: instrumentId, roles: [role] };
            const mixRole = mixAgent.current.getRole(instDef);
            mixAgent.current.register(key, mixRole, currentSynth);

            const part = new Tone.Part((time, ev) => {
                // Call notification callback
                if (onNoteEvent) {
                    // Throttling to prevent React render lag (max 30fps ~33ms)
                    const now = Date.now();
                    if (now - lastNoteEventTime.current > 33) {
                        lastNoteEventTime.current = now;
                        Tone.Draw.schedule(() => {
                            onNoteEvent({ ...ev, instanceId: key });
                        }, time);
                    }
                }

                const dur = ev.len * Tone.Time("4n").toSeconds();

                // Synth might be disposed if race condition
                if (!currentSynth || currentSynth.disposed) return;

                // Handle Automation (CC) - REMOVED (Handled in previous update, but confirming here)
                if (ev.type === 'cc') return;

                const velocity = (ev.vel !== undefined ? ev.vel : (ev.velocity !== undefined ? ev.velocity : 0.7));

                // --- KEYSWITCH INTERCEPTION ---
                // Case 1: Property-based Keyswitch (Internal Engine)
                // Fix: Check 'articulation' (String) first, then 'ks' (Number), then legacy 'keyswitch'
                if (currentSynth.setArticulation) {
                    if (ev.articulation) {
                        currentSynth.setArticulation(ev.articulation);
                    } else if (ev.ks !== undefined) {
                        currentSynth.setArticulation(ev.ks);
                    } else if (ev.keyswitch !== undefined) {
                        currentSynth.setArticulation(ev.keyswitch);
                    }
                }

                // Case 2: Note-based Keyswitch (Input Interceptor)
                // Filter out low notes intended for articulation control to prevent "Keyswitch Leak"
                if (ev.d !== undefined && typeof ev.d === 'number' && ev.d < KEYSWITCH_MAX_NOTE) {
                    if (currentSynth.setArticulation) {
                        currentSynth.setArticulation(ev.d);
                    }
                    return; // BLOCK AUDIO
                }

                // --- DRUM & INSTRUMENT LOGIC ---
                // Support for Samplers requiring specific Notes vs NoiseSynths requiring duration
                const isSampler = instrumentId === 'drums'; // Specific check for Drum Kit Sampler

                if (isSampler) {
                    // Use actual MIDI Note for Sampler
                    const note = getNoteName(ev.d || 36);
                    if (note && currentSynth.triggerAttackRelease) {
                        currentSynth.triggerAttackRelease(note, dur, time, velocity);
                    }
                }
                else if (role.toLowerCase().includes('kick') || role === 'perc_low') {
                    if (currentSynth.triggerAttackRelease) currentSynth.triggerAttackRelease("C1", dur, time, velocity);
                } else if (role.toLowerCase().includes('snare') || role === 'perc_mid') {
                    if (currentSynth.triggerAttackRelease) currentSynth.triggerAttackRelease("8n", time, velocity);
                } else if (role.toLowerCase().includes('hat') || role === 'perc_high') {
                    if (currentSynth.triggerAttackRelease) currentSynth.triggerAttackRelease("C6", "32n", time, velocity * 0.5);
                } else {
                    const note = getNoteName(ev.d);
                    if (note && currentSynth.triggerAttackRelease) currentSynth.triggerAttackRelease(note, dur, time, velocity);
                }

            }, instEvents.map(e => {
                // Apply Groove Offset (playbackOffset) if available
                const startTime = (e.s + (e.playbackOffset || 0)) * Tone.Time("4n").toSeconds();
                return { ...e, time: startTime };
            }));

            part.start(0);
            parts.current.push(part);
        });
    };

    const play = async (composition, bpm = 120, onNoteEvent = null) => {
        if (!isReady) return;

        await Tone.loaded();

        Tone.Transport.bpm.value = bpm;
        Tone.Transport.stop();
        Tone.Transport.cancel();

        if (composition) {
            compositionRef.current = composition; // Store for Context Loop

            // Apply Auto Mix Strategy
            if (masteringChain.current) {
                const mixConfig = applyAutoMix(composition.genre);

                // Configure Master Bus
                if (mixConfig.masterBus) {
                    masteringChain.current.configure(mixConfig.masterBus);
                }

                // Configure Global Reverb
                if (mixConfig.globalReverb && globalReverb.current) {
                    const r = mixConfig.globalReverb;
                    const node = globalReverb.current;

                    if (node.name === 'Reverb' || node.name === 'JCReverb') {
                        if (r.decay) node.decay = r.decay;
                        if (r.preDelay) node.preDelay = r.preDelay;
                        if (r.wet !== undefined) node.wet.rampTo(r.wet, 0.5);
                    } else if (node.name === 'Compressor') {
                        // Mobile Fallback: Map reverb params to compressor settings?
                        // Just ensure it's not silent.
                    }
                }

                // Configure Global Delay
                if (mixConfig.globalDelay && globalDelay.current) {
                    const d = mixConfig.globalDelay;
                    const node = globalDelay.current;

                    if (d.delayTime) node.delayTime.value = d.delayTime;
                    if (d.feedback) node.feedback.value = d.feedback;
                    if (d.wet !== undefined) node.wet.rampTo(d.wet, 0.5);
                }
            }

            scheduleComposition(composition, onNoteEvent);
        }

        Tone.Transport.start("+0.1");
        setIsPlaying(true);
    };

    const stop = () => {
        Tone.Transport.stop();
        setIsPlaying(false);
        if (watchdogId.current) {
            cancelAnimationFrame(watchdogId.current);
            watchdogId.current = null;
        }
    };

    const setMasterVolume = (db) => {
        if (masterVol.current) {
            masterVol.current.volume.rampTo(db, 0.1);
        }
    };

    // Helpers for animation loops (avoiding React state)
    const getTransportTime = useCallback(() => {
        // Compensate for visual lag by adding lookahead?
        // Actually, if it feels "behind", it means the Visuals are drawing t=0 when Audio is at t=0.1
        // Wait, if audio is buffered, we hear it LATER.
        // So visual needs to be delayed to match audio?
        // Or if Tone.Transport.seconds reports schedule time (future), visuals should be AHEAD.
        // User says visuals are "a bar behind". This is huge lag.
        // It implies Tone.Transport.seconds is reporting a value much smaller than expected, OR
        // the visualizer is scaling incorrectly.

        // Let's rely on Tone.draw if possible, but for smooth CSS animation we need continuous time.
        // Tone.now() - Tone.context.lookAhead?
        // Let's try returning raw seconds. The visualizer code was simple.

        // Fix: Use Tone.Transport.seconds.
        // If it's a bar behind, maybe the loop logic in visualizer is wrong?
        // No, visualizer just does (t / totalDuration) * 100.

        return Tone.Transport.seconds;
    }, []);

    const getTransportPosition = useCallback(() => {
        // Return current position in ticks (independent of BPM changes for beat calc)
        return Tone.Transport.ticks;
    }, []);

    const getCurrentBar = useCallback(() => {
        const pos = Tone.Transport.position.split(':');
        const bars = parseFloat(pos[0]);
        const beats = parseFloat(pos[1]);
        const sixteenths = parseFloat(pos[2]);
        return bars + (beats / 4) + (sixteenths / 16);
    }, []);

    const seek = useCallback((time) => {
        if (Tone.Transport.state !== 'stopped') {
            Tone.Transport.seconds = time;
        }
    }, []);

    return {
        isReady,
        startAudio,
        play,
        stop,
        seek, // Exposed seek function
        isPlaying,
        getTransportTime,
        getTransportPosition,
        getCurrentBar,
        setMasterVolume,
        setEnsemble,
        setMacro
    };
};
